import { useState, useEffect } from 'react'
import AdminTable from '../../components/admin/AdminTable'
import AdminForm from '../../components/admin/AdminForm'
import { motion, AnimatePresence } from 'framer-motion'

const API = import.meta.env.VITE_API_URL || '/api'
function getHeaders() { const t = localStorage.getItem('token'); return { 'Content-Type': 'application/json', Authorization: t ? `Bearer ${t}` : '' } }

const fields = [
  { name: 'title', label: 'Title', type: 'text' as const, required: true },
  { name: 'slug', label: 'Slug', type: 'text' as const, placeholder: 'my-blog-post' },
  { name: 'category', label: 'Category', type: 'text' as const },
  { name: 'author', label: 'Author', type: 'text' as const },
  { name: 'date', label: 'Date', type: 'text' as const, placeholder: '2025-12-01' },
  { name: 'cover_image', label: 'Cover Image URL', type: 'text' as const },
  { name: 'excerpt', label: 'Excerpt', type: 'textarea' as const },
  { name: 'content', label: 'Content (Markdown)', type: 'textarea' as const },
  { name: 'tags', label: 'Tags (JSON array)', type: 'json' as const },
  { name: 'published', label: 'Published', type: 'checkbox' as const },
]

export default function AdminBlog() {
  const [items, setItems] = useState<any[]>([])
  const [editing, setEditing] = useState<any | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const fetchItems = async () => { const r = await fetch(`${API}/blog`); setItems(await r.json()); setLoading(false) }
  useEffect(() => { fetchItems() }, [])

  const handleSave = async (data: any) => {
    setSaving(true); const isNew = !editing
    const slug = data.slug || data.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
    const r = await fetch(isNew ? `${API}/blog` : `${API}/blog/${data.id}`, {
      method: isNew ? 'POST' : 'PUT', headers: getHeaders(), body: JSON.stringify({ ...data, slug }),
    })
    if (r.ok) { await fetchItems(); setShowForm(false); setEditing(null) }
    setSaving(false)
  }

  const handleDelete = async (item: any) => { await fetch(`${API}/blog/${item.id}`, { method: 'DELETE', headers: getHeaders() }); await fetchItems() }

  const columns = [
    { key: 'title', label: 'Title', render: (v: string) => <span className="font-medium">{v}</span> },
    { key: 'category', label: 'Category', render: (v: string) => <span className="text-xs bg-white/10 px-2 py-1 rounded">{v}</span> },
    { key: 'date', label: 'Date' },
    { key: 'published', label: 'Status', render: (v: boolean) => v ? <span className="text-green-400 text-xs">Published</span> : <span className="text-gray-500 text-xs">Draft</span> },
  ]

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Blog Posts</h1>
        <button onClick={() => { setEditing(null); setShowForm(true) }} className="px-4 py-2 bg-electric-blue hover:bg-electric-blue/80 text-white rounded-lg text-sm">+ New Post</button>
      </div>
      <div className="bg-gray-900 border border-white/10 rounded-xl p-5">
        <AdminTable columns={columns} data={items} onEdit={(i) => { setEditing(i); setShowForm(true) }} onDelete={handleDelete} loading={loading} />
      </div>
      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center pt-20 p-4 overflow-y-auto">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-2xl">
              <h2 className="text-xl font-bold text-white mb-6">{editing ? 'Edit Post' : 'New Post'}</h2>
              <AdminForm fields={fields} initial={editing || { id: `post-${Date.now()}`, title: '', slug: '', excerpt: '', content: '', cover_image: '', author: '', date: new Date().toISOString().split('T')[0], category: '', tags: [], published: false }} onSubmit={handleSave} onCancel={() => { setShowForm(false); setEditing(null) }} loading={saving} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
